import { faXmark } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';

import { AppButton } from './AppButton';
import { IconButton } from './IconButton';

// Every dialog in the app (OQ-181, ui/app-components.md § Shared UI components).
type AppDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  confirmVariant?: 'primary' | 'danger';
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  busy?: boolean;
  cancelLabel?: string;
  footer?: React.ReactNode;
  size?: string;
  centered?: boolean;
  scrollable?: boolean;
};

export const AppDialog = ({
  isOpen,
  onClose,
  title,
  children,
  confirmLabel,
  confirmVariant = 'primary',
  onConfirm,
  confirmDisabled,
  busy,
  cancelLabel,
  footer,
  size,
  centered,
  scrollable
}: AppDialogProps) => {
  const { t } = useTranslation();
  const titleId = React.useId();
  const close = () => {
    if (!busy) onClose();
  };
  return (
    <Modal
      isOpen={isOpen}
      toggle={close}
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
      labelledBy={titleId}
      size={size}
      centered={centered}
      scrollable={scrollable}
    >
      <div className='modal-header'>
        <h2 id={titleId} className='modal-title fs-5 fw-semibold'>
          {title}
        </h2>
        <IconButton
          icon={faXmark}
          label={t('common.close')}
          variant='ghost'
          size='sm'
          onClick={close}
          disabled={busy}
        />
      </div>
      {children !== undefined && <ModalBody>{children}</ModalBody>}
      <ModalFooter>
        {footer ??
          (onConfirm ? (
            <>
              <AppButton variant='ghost' onClick={close} disabled={busy}>
                {cancelLabel ?? t('common.cancel')}
              </AppButton>
              <AppButton variant={confirmVariant} onClick={onConfirm} disabled={confirmDisabled} loading={busy}>
                {confirmLabel}
              </AppButton>
            </>
          ) : (
            <AppButton variant='ghost' onClick={close}>
              {t('common.close')}
            </AppButton>
          ))}
      </ModalFooter>
    </Modal>
  );
};
